<template>
  <mf-loading-dialog :loading="loadingPage">
    <base-page-layout
      :title="$t('campanhas.resultados_campanha')"
      class="results-page"
      :hide-breadcrumbs="$getFirstValue()"
      use-new-filters
      clear-filter-on-init
      :filters="['lojas']"
      hide-ecommerce-configuration
      :filter-to-units="campaignUnitIds"
      check-all-units-on-init
      show-tour
      :flow-id="flowId"
      :loading-tutorial="loadingTutorial"
      @ecommerce-config-changed="handleEcommerceConfigChanged"
    >
      <template v-if="['ACTIVE', 'FINISHED'].includes(campaign.status)" v-slot:title-complement>
        <div class="pl-3">
          <mf-button
            v-if="campaign.status === 'ACTIVE'"
            label="Editar campanha"
            outlined
            text-transform="none"
            icon="edit"
            icon-position="left"
            @click="emitEditFunction"
          />
          <mf-button
            v-else-if="campaign.status === 'FINISHED'"
            label="Visualizar campanha"
            outlined
            text-transform="none"
            icon="visibility"
            icon-position="left"
            @click="emitViewFunction"
          />
        </div>
      </template>
      <maria-results-header :campaign="campaign" />

      <v-row>
        <v-col class="mt-5 mb-2 pl-4 pt-0 pb-0">
          <span class="maria-section-title">Resultados gerais</span>
        </v-col>
      </v-row>

      <v-row>
        <v-col v-for="(card, index) in dataCards" :key="index" cols="3">
          <cbm-card
            :number-text="card.value"
            :title="card.text"
            :tooltip="card.helpText"
            :boolean-loader="loadingPage"
            :quick-action="card.quickAction"
            :is-currency="card.isCurrency"
            :percent-text="card.underText"
            :percent-description="card.description"
            unset-percentage-colors
            simple-card
          />
        </v-col>
        <v-col cols="12">
          <customers-types-funnel-graph
            :campaign="campaign"
            :metrics="metrics"
            :loading="loadingFilter || loadingPage"
            :date-criteria-filter="dateCriteriaFilter"
            :channels="stateFilters.channels"
            :units="stateFilters.units"
            :units-configuration="stateFilters.unitsConfiguration"
          />
        </v-col>
      </v-row>

      <v-card class="mt-3">
        <product-billing-history
          :campaign="campaign"
          :units="stateFilters.units"
          :loading="loadingFilter"
          :units-configuration="stateFilters.unitsConfiguration"
        />
      </v-card>

      <v-row class="mt-n4">
        <v-col class="pl-4">
          <span class="maria-section-title">
            Clientes que
            <span class="select-label">
              <v-select
                v-model="chosenCustomerType"
                color="marca-0-l40"
                :items="customersTypes"
                append-icon="expand_more"
                hide-details
                item-color="marca-0-l40"
                item-value="value"
                item-text="text"
              />
            </span>
          </span>
        </v-col>
      </v-row>

      <v-row align-content="stretch">
        <v-col cols="6">
          <gender-and-age-metrics-chart :data-chart="ticketByGenderAndAge" :chosen-customer-type="chosenCustomerType" :loading="loadingFilter" />
        </v-col>

        <v-col cols="6">
          <status-card
            :data-chart="metrics.clients_metrics"
            :chosen-customer-type="chosenCustomerType"
            :loading="loadingFilter"
            :campaign="campaign"
            :date-criteria-filter="dateCriteriaFilter"
            :units="stateFilters.units"
            :units-configuration="stateFilters.unitsConfiguration"
          />
        </v-col>

        <v-col cols="7">
          <customer-billing-triggers
            :campaign="campaign"
            :metrics="metrics"
            :loading="loadingFilter"
            :chosen-customer-type="chosenCustomerType"
            :date-criteria-filter="dateCriteriaFilter"
            :channels="stateFilters.channels"
            :units="stateFilters.units"
            :units-configuration="stateFilters.unitsConfiguration"
          />
        </v-col>

        <v-col cols="5">
          <receive-channel-chart
            :data-chart="metrics.general_metrics"
            :campaign="campaign"
            :chosen-customer-type="chosenCustomerType"
            :loading="loadingFilter"
          />
        </v-col>

        <v-col cols="12">
          <time-biggest-purchase-chart
            :data-chart="ticketsByHour"
            :chosen-customer-type="chosenCustomerType"
            :loading="loadingFilter"
            metrics-period="YEARLY"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col class="pl-4">
          <span class="maria-section-title">Resultados por produtos</span>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="7">
          <related-products class="fill-height" :data-chart="metrics.products_metrics" :loading="loadingFilter" />
        </v-col>

        <v-col cols="5">
          <average-basket-items class="fill-height" :data-card="itemsBasket" :loading="loadingFilter" />
        </v-col>
      </v-row>

      <results-by-products
        :metrics="metrics.products_metrics"
        :loading="loadingFilter"
        :campaign="campaign"
        :date-criteria-filter="dateCriteriaFilter"
        :units="stateFilters.units"
        :units-configuration="stateFilters.unitsConfiguration"
      />

      <v-row>
        <v-col class="pl-4">
          <span class="maria-section-title">Canais</span>
        </v-col>
      </v-row>

      <channel-triggers class="mt-3" :campaign="campaign" :metrics="metrics" :loading="loadingFilter" />

      <mf-action-buttons
        class="mt-3"
        :primary-button="{
          text: 'Duplicar',
          action: cloneOmniCampaign,
          isVisible: true,
          isLoading: loadingCloneCampaign,
        }"
        :cancel-button="{
          text: 'Fechar',
          action: backToOffersList,
          isVisible: true,
          isLoading: loadingCloneCampaign,
        }"
      />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { DateTime } from 'luxon'
import { mapMutations, mapState } from 'vuex'

import {
  QUERY_GET_OMNI_CAMPAIGN,
  QUERY_GET_OMNI_CAMPAIGN_TICKETS_BY_HOURS,
  QUERY_GET_OMNI_CAMPAIGN_CHANNELS_ITEMS,
  QUERY_GET_OMNI_CAMPAIGN_TICKETS_BY_GENDER_AND_AGE,
  QUERY_METRICS_OMNI_CAMPAIGN_BY_UNITS,
  QUERY_CLONE_OMNI_CAMPAIGN,
} from '@/modules/maria/campanha-da-maria-v2/graphql'
import BasePageLayout from '@/components/BasePageLayout.vue'
import { criteriaGroup, campaignReceivedAndPurchasedAtUnitsOnFixedPeriodCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { localeString, getStatusLabel, getPeriod, floatToPercentString, putEmptyValue, assertArray } from '@/config/helpers.js'
import { mergeAllComboProducts } from '@/modules/campanhas/gestao-ofertas-v2/helpers/products.js'

export default {
  name: 'Results',
  components: {
    BasePageLayout,
    CbmCard: () => import('@/components/CbmCard.vue'),
    MariaResultsHeader: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/MariaResultsHeader.vue'),
    StatusCard: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/StatusCard.vue'),
    RelatedProducts: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/RelatedProducts.vue'),
    ChannelTriggers: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/ChannelTriggers.vue'),
    ResultsByProducts: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/ResultsByProducts.vue'),
    AverageBasketItems: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/AverageBasketItems.vue'),
    ReceiveChannelChart: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/ReceiveChannelChart.vue'),
    ProductBillingHistory: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/ProductBillingHistory.vue'),
    CustomerBillingTriggers: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/CustomerBillingTriggers.vue'),
    GenderAndAgeMetricsChart: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/GenderAndAgeMetricsChart.vue'),
    TimeBiggestPurchaseChart: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/TimeBiggestPurchaseChart.vue'),
    CustomersTypesFunnelGraph: () => import('@/modules/maria/campanha-da-maria-v2/views/components/Results/CustomersTypesFunnelGraph.vue'),
  },
  data: () => ({
    loadingCloneCampaign: false,
    metrics: {},
    customersTypes: [
      {
        text: 'receberam e compraram',
        value: 'received_and_bought',
      },
      {
        text: 'receberam e compraram os produtos da(s) oferta(s)',
        value: 'received_and_bought_with_offer_products',
      },
      {
        text: 'receberam e compraram o produto que receberam via variável na mensagem',
        value: 'received_and_bought_with_received_product',
      },
    ],
    itemsBasket: [],
    chosenCustomerType: 'received_and_bought',
    ticketByGenderAndAge: {},
    ticketsByHour: [],
    locale: localeString,
    DateTime: DateTime,
    showAllProducts: false,
    showAllUnits: false,
    showAllSegments: false,
    campaign: {},
    loadingPage: true,
    loadingFilter: false,
    loadingTutorial: false,
    // Valores do e-commerce recebidos do MfSelectUnits
    ecommerceConfig: {
      includeEcommerce: true,
      onlyEcommerce: false,
    },
  }),
  computed: {
    ...mapState({
      stateUnits: (state) => state.units,
      stateFilters: (state) => state.filters,
    }),
    flowId() {
      const flowId = 'fac6e041-b703-4451-af9e-410e31df4c6b'
      return flowId
    },
    campaignUnitIds() {
      if (!this.campaign?.units?.length) {
        return []
      }
      return this.campaign.units.map((unit) => unit.id_erp)
    },
    dateCriteriaFilter() {
      if (!this.campaign?.initial_date || !this.campaign?.final_date) {
        return {}
      }
      const formattedInitialDate = DateTime.fromFormat(this.campaign?.initial_date, 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const formattedFinalDate = DateTime.fromFormat(this.campaign?.final_date, 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const formattedADayBeforeCampaignStart = DateTime.fromFormat(this.campaign?.initial_date, 'yyyy-MM-dd').plus({ day: -1 }).toFormat('yyyyMMdd')

      return { formattedInitialDate, formattedFinalDate, formattedADayBeforeCampaignStart }
    },
    dataCards() {
      return [
        {
          value: localeString(this.metrics.clients_metrics?.general.received_and_bought.count || 0),
          text: 'Clientes que compraram',
          underText: `${localeString(
            parseFloat((this.metrics.clients_metrics?.general.received_and_bought.count / this.metrics.clients_metrics?.general.received.count) * 100) || 0,
            1
          )}%`,
          description: `dos ${this.metrics.clients_metrics?.general.received.count.toLocaleString('pt-BR')} clientes que receberam`,
          helpText: 'Quantidade de clientes que receberam algum disparo e realizaram pelo menos uma compra no período da campanha nas lojas selecionadas',
          quickAction: {
            id: 'received_and_purchased_any_campaign_maria_campaign',
            shortDescription: `Clientes que receberam campanhas e compraram`,
            longDescription: `Clientes que compraram na campanha ${this.campaign.title}`,
            segmentationGroup: criteriaGroup(
              campaignReceivedAndPurchasedAtUnitsOnFixedPeriodCriteria(
                [this.campaign._id],
                this.dateCriteriaFilter.formattedInitialDate,
                this.dateCriteriaFilter.formattedFinalDate,
                this.stateFilters.unitsConfiguration
              )
            ),
          },
        },
        {
          value: localeString(this.metrics.clients_metrics?.general.received_and_bought.average_ticket || 0, 2),
          text: 'Ticket médio',
          isCurrency: true,
          underText: `${localeString(
            this.metrics.analytic_metrics?.general.average_ticket_received_variation > 0
              ? this.metrics.analytic_metrics?.general.average_ticket_received_variation
              : this.metrics.analytic_metrics?.general.average_ticket_received_variation < 0
              ? this.metrics.analytic_metrics?.general.average_ticket_received_variation * -1
              : 0,
            1
          )}%`,
          description: `a ${this.metrics.analytic_metrics?.general.average_ticket_received_variation > 0 ? 'mais' : 'menos'} que quem não recebeu`,
          helpText:
            'Ticket médio dos clientes que receberam a campanha e realizaram pelo menos uma compra no período de mensuração em comparação com o ticket médio dos consumidores do clube no mesmo período dessa campanha',
        },
        {
          value: localeString(this.metrics.clients_metrics?.general.received_and_bought.revenue || 0, 0),
          text: 'Faturamento',
          isCurrency: true,
          isInteger: true,
          underText: `${localeString(
            parseFloat((this.metrics.clients_metrics?.general.received_and_bought.revenue / this.metrics.clients_metrics?.general.total.revenue) * 100 || 0),
            1
          )}%`,
          description: 'de faturamento total',
          helpText:
            'Faturamento dos clientes que receberam e compraram em comparação com o faturamento dos consumidores do clube no mesmo período dessa campanha',
        },
        {
          value: localeString(this.valueLeverage || 0),
          text: 'Alavancagem',
          underText: this.underTextValueLeverage,
          description: this.textLeverage,
          helpText: 'Faturamento total gerado para cada real investido na campanha. Considera apenas o faturamento gerado pelos canais com custo unitário',
        },
      ]
    },
    isCostChannels() {
      return !this.campaign.messages_dispatches_channels?.every((channel) => ['whatsapp', 'push'].includes(channel))
    },
    valueLeverage() {
      return this.isCostChannels ? this.metrics.analytic_metrics?.general.received_roi : '-'
    },
    textLeverage() {
      return this.isCostChannels ? `Investimento da campanha` : 'Os canais não possuem custo unitário'
    },
    underTextValueLeverage() {
      return this.isCostChannels ? `R$${localeString(this.metrics.analytic_metrics?.general.price || 0, 0)}` : null
    },
  },

  watch: {
    'stateFilters.units': {
      async handler(value) {
        this.loadingFilter = true
        const resultMetrics = await this.mountQuery(QUERY_METRICS_OMNI_CAMPAIGN_BY_UNITS)
        const { metrics } = resultMetrics.data.omniCampaign
        this.metrics = metrics
        this.loadingPage = false
        this.loadingFilter = false
      },
    },
  },

  async mounted() {
    this.getData()
    this.$bus.$emit('filter')
  },

  destroyed() {
    this.RESET_MARIA_CAMPAIGN_MODULE()
  },

  methods: {
    getPeriod,
    getStatusLabel,
    floatToPercentString,
    putEmptyValue,
    criteriaGroup,
    campaignReceivedAndPurchasedAtUnitsOnFixedPeriodCriteria,

    ...mapMutations(['SET_MARIA_CAMPAIGN', 'RESET_MARIA_CAMPAIGN_MODULE']),

    backToOffersList() {
      this.$router.push({ name: 'maria-campaign' })
    },

    async cloneOmniCampaign() {
      const confirmed = await this.$confirm({
        ...this.$t('campanhas.confirmations.clone'),
        message: `Deseja duplicar ${this.campaign.name ? 'a campanha ' + this.campaign.name.trim().bold() : 'esta campanha'}?`,
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.loadingCloneCampaign = true
      await this.$apollo
        .mutate({
          mutation: QUERY_CLONE_OMNI_CAMPAIGN,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
          variables: {
            id: this.campaign._id,
          },
        })
        .then((response) => {
          this.loadingCloneCampaign = false
          const id = response.data.cloneOmniCampaign._id
          this.$router.push({ name: 'Edit Mar.ia campaign', params: { id } })
        })
        .catch(() => {
          this.loadingCloneCampaign = false
          Swal.fire({
            text: 'Erro ao duplicar campanha',
            icon: 'error',
            showConfirmButton: false,
            timer: 1000,
            customClass: {
              content: 'content-class',
            },
          })
        })
    },

    formatOfferSales(data) {
      let offerSales = []
      data.forEach((offer) => {
        if (['CampaignOfferSalesGroup', 'OfferSalesGroup'].includes(offer.__typename) || offer?.isGroup) {
          offerSales.push(
            ...offer.offer_sales.map((offerGroup) => {
              return {
                ...offerGroup,
                price_1: offer.price_1,
                price_2: offer.price_2,
                isGroup: true,
                nameGroup: offer.name,
                groupInfo: {
                  _id: offer._id,
                  __typename: 'ProductsGroup',
                },
              }
            })
          )
        } else offerSales.push(offer)
      })
      return offerSales
    },

    formatTakePayOfferSales(data) {
      let takepay = []
      data.forEach((offer) => {
        if (['CampaignOfferSalesTakeAndPayGroup', 'OfferSalesTakeAndPayGroup'].includes(offer.__typename)) {
          takepay.push(
            ...offer.takepay_offer_sales.map((offerGroup) => {
              return {
                ...offerGroup,
                take: offer.take,
                pay: offer.pay,
                isGroup: true,
                nameGroup: offer.name,
                groupInfo: {
                  _id: offer._id,
                  __typename: 'ProductsGroup',
                },
              }
            })
          )
        } else takepay.push(offer)
      })
      return takepay
    },

    formatPercentageOfferSales(data) {
      let percentage = []
      data.forEach((offer) => {
        if (['CampaignOfferSalesPercentageGroup', 'OfferSalesPercentageGroup'].includes(offer.__typename)) {
          percentage.push(
            ...offer.percentage_offer_sales.map((offerGroup) => {
              return {
                ...offerGroup,
                percentage_discount: offer.percentage_discount,
                isGroup: true,
                nameGroup: offer.name,
                groupInfo: {
                  _id: offer._id,
                  __typename: 'ProductsGroup',
                },
              }
            })
          )
        } else percentage.push(offer)
      })
      return percentage
    },

    formatComboOfferSales(data) {
      const comboOfferSalesProducts = assertArray(data).reduce((productList, product) => {
        productList.push(...mergeAllComboProducts(product.target_products, 'target_products'))
        productList.push(...mergeAllComboProducts(product.trigger_products, 'trigger_products'))

        return productList
      }, [])
      return comboOfferSalesProducts
    },

    async getData() {
      this.loadingPage = true
      try {
        const [resultOmniCampaign, resultMetrics, resultChannelsItems, resultsTicketsHours, resultsTicketsGenderAge] = await Promise.all([
          this.getCampaign(),
          this.mountQuery(QUERY_METRICS_OMNI_CAMPAIGN_BY_UNITS),
          this.mountQuery(QUERY_GET_OMNI_CAMPAIGN_CHANNELS_ITEMS),
          this.mountQuery(QUERY_GET_OMNI_CAMPAIGN_TICKETS_BY_HOURS),
          this.mountQuery(QUERY_GET_OMNI_CAMPAIGN_TICKETS_BY_GENDER_AND_AGE),
        ])
        const { omniCampaign } = resultOmniCampaign.data
        this.campaign = {
          ...omniCampaign,
          offer_sales: this.formatOfferSales(omniCampaign.offer_sales),
          takepay_offer_sales: this.formatTakePayOfferSales(omniCampaign.takepay_offer_sales),
          percentage_offer_sales: this.formatPercentageOfferSales(omniCampaign.percentage_offer_sales),
          combo_offer_sales: this.formatComboOfferSales(omniCampaign.combo_offer_sales),
        }
        this.SET_MARIA_CAMPAIGN(this.campaign)

        const { metrics } = resultMetrics.data.omniCampaign
        this.metrics = metrics

        this.itemsBasket = resultChannelsItems.data.omniCampaignAverageItemsByClientMessageReceiving.metrics
        this.ticketsByHour = resultsTicketsHours.data.omniCampaignTicketsByHour.metrics
        this.ticketByGenderAndAge = resultsTicketsGenderAge.data.omniCampaignAverageTicketByGenderAndAge
      } catch (error) {
        console.error(error)
        Swal.fire({
          text: 'Erro ao buscar campanha',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
      } finally {
        this.loadingPage = false
      }
    },

    getCampaign() {
      return this.$apollo.query({
        query: QUERY_GET_OMNI_CAMPAIGN,
        variables: {
          id: this.$route.params.id,
        },
        fetchPolicy: 'no-cache',
      })
    },

    mountQuery(query) {
      return this.$apollo.query({
        query: query,
        variables: {
          id: this.$route.params.id,
          units: this.stateFilters.units,
          includeEcommerce: this.ecommerceConfig.includeEcommerce,
          onlyEcommerce: this.ecommerceConfig.onlyEcommerce,
        },
        fetchPolicy: 'no-cache',
      })
    },

    emitEditFunction() {
      const route = this.$router.resolve({ name: 'edit-maria-campaign', params: { id: this.campaign._id } })
      window.open(route.href, '_blank')
    },

    emitViewFunction() {
      const route = this.$router.resolve({ name: 'view-maria-campaign', params: { id: this.campaign._id } })
      window.open(route.href, '_blank')
    },

    handleEcommerceConfigChanged(config) {
      this.ecommerceConfig = {
        includeEcommerce: config.includeEcommerce,
        onlyEcommerce: config.onlyEcommerce,
      }
      // Os valores são armazenados e serão utilizados na próxima execução do filtro
    },
  },
}
</script>

<style lang="scss" scoped>
.v-select__selection--comma {
  overflow: inherit !important;
}

.select-label {
  width: min-content;
  color: $marca-0-l40;
  display: inline-flex;
  cursor: pointer;
}

.table-card-title {
  font-family: 'Lato', sans-serif !important;
  font-style: normal;
  font-weight: normal !important;
  font-size: 18px !important;
  line-height: 120% !important;
  color: $cinza-d10 !important;
}

.subtitle {
  font-family: 'Lato', sans-serif !important;
  font-size: 16px !important;
  color: $cinza-0 !important;
  word-break: break-word !important;
}

.btn-link {
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  display: flex;
  align-items: center;
  letter-spacing: 0.01em;
  text-decoration-line: underline;
  color: $marca-0-l40 !important;
}

.campaign-description {
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 120%;
  letter-spacing: 0.4px;
  color: $cinza-0;
}

.info-header {
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 120%;
  color: $cinza-d10;
}

.info-description {
  font-family: Lato !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 14px !important;
  line-height: 150% !important;
  color: $cinza-0 !important;
}

::v-deep .v-input--switch.v-input--is-dirty.v-input--is-disabled {
  opacity: 1;

  ::v-deep .v-input--selection-controls__input {
    opacity: 0.6 !important;
  }
}

.results-page ::v-deep .v-input--selection-controls {
  margin: 0 !important;
  padding: 0 !important;
  cursor: none !important;
}

.maria-section-title {
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
  color: #4b525a;

  div {
    color: $marca-0-l40 !important;
  }
}
</style>
